import { getIdTokenUsingQueryClient, queryKeys } from '@/data/queries'
import { useDocumentStore } from '@/stores/documentStore'
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
  useParams,
  useRouteContext,
  useSearch,
} from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DocumentEditHeader } from '@/components/document-edit/DocumentEditHeader'
import { LoadingDocument } from '@/components/loading-ui/LoadingDocument'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import TopNavDocumentEdit from '@/components/document-edit/TopNavDocumentEdit'
import { DraftModeBanner } from '@/components/document-edit/DraftModeBanner'
import { OlderVersionBanner } from '@/components/document-edit/OlderVersionBanner'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useLastViewedFromLocal } from '@/hooks/useLastViewedFromLocal'
import { updateSOP } from '@/data/mutations'
import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import {
  documentByIdOptions,
  documentEmbeddingStatusOptions,
  documentVersionHistoryByVersionOptions,
  orgPermissionsCurrentUserOptions,
  referenceDocumentsByIDsOptions,
} from '@/data/queryOptions'
import { getProjectRoles } from '@/data/entities'
import { hasUnpublishedContentChanges } from '@/lib/documentContent'
import { BASE_URL } from '@/data/constants'
import { VideoPlayerScope, type VideoSrcUrls } from '@/components/video-player'
import { SidebarProvider } from '@/components/ui/sidebar'
import { VersionHistorySidebar } from '@/components/document-edit/VersionHistorySidebar'
import { z } from 'zod'
import {
  ProcessingStepper,
  type ProcessingPhase,
} from '@/components/document-edit/ProcessingStepper'
import { PostProcessBanner } from '@/components/document-edit/PostProcessBanner'
import { PROCESSING_POLL_INTERVAL } from '@/data/constants'

type SOPRouteSearch = {
  version?: string
}

const SOPRouteSearchSchema = z
  .object({
    version: z
      .string()
      .regex(/^(draft|\d+)$/)
      .optional(),
  })
  .partial()

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId',
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { queryClient, authState } = context
    if (!params.sopId) {
      throw new Error('Error fetching document, no sopId')
    }

    const TENANT_ID = params.orgId
    const store = useSelectedTenantStore.getState()
    // Sync URL tenant to store if different
    if (TENANT_ID && store.selectedTenantId !== TENANT_ID) {
      store.setSelectedTenantId(TENANT_ID)
    }

    if (!TENANT_ID) {
      throw redirect({ to: '/select-tenant' })
    }

    const idToken = await getIdTokenUsingQueryClient({
      queryClient,
      authState: authState!,
    })

    return queryClient.ensureQueryData(
      documentByIdOptions(idToken, TENANT_ID, params.projectId!, params.sopId!),
    )
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.title} - LineWise`,
      },
      {
        name: 'description',
        content: `Document - ${loaderData?.title}`,
      },
    ],
  }),
  validateSearch: (search: SOPRouteSearch) => {
    return SOPRouteSearchSchema.parse(search)
  },
  pendingComponent: () => <LoadingDocument />,
})

type DocumentState =
  | {
      type: 'processing'
      phases: ProcessingPhase[]
    }
  | {
      type: 'error'
      message: string
    }
  | {
      type: 'ready'
    }

function RouteComponent() {
  const { authState, isOrgAdmin, isOrgEditor } = useRouteContext({
    from: '/_authenticated',
  })
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const { sopId: docId, projectId } = useParams({
    from: '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId',
  })
  const navigate = useNavigate()
  const { version } = useSearch({
    from: '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId',
  })

  // Diagnostics is the only child tab that must remain reachable while the
  // SOP is in the ERROR branch — it exists specifically to debug failed
  // generations. All other child tabs (Details/Steps/Full video/Insights)
  // stay gated on `state.type === 'ready'` below.
  const matchRoute = useMatchRoute()
  const onDiagnosticsTab = !!matchRoute({
    to: '/org/$orgId/projects/$projectId/sops/$sopId/diagnostics',
    fuzzy: true,
  })

  const { data: orgPermissions } = useSuspenseQuery(
    orgPermissionsCurrentUserOptions(TENANT_ID!, idToken!),
  )
  const currentUserProjectRoles = useMemo(
    () => getProjectRoles(orgPermissions, TENANT_ID!),
    [orgPermissions, TENANT_ID],
  )
  const canEditOrg = isOrgAdmin || isOrgEditor
  const projectRole = currentUserProjectRoles.get(projectId)?.toUpperCase()
  const isProjectAdmin = projectRole === 'ADMIN'
  const isProjectEditor = isProjectAdmin || projectRole === 'EDITOR'
  const canEdit = canEditOrg || isProjectEditor
  const canPublish = isOrgAdmin || isProjectAdmin
  const documentContainerRef = useRef<HTMLDivElement>(null)

  // Scroll to top when version changes
  useEffect(() => {
    if (documentContainerRef.current) {
      console.log('version changed, scrolling to top', version)
      documentContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [version])

  // For users without edit permissions, force them to see only published version
  useEffect(() => {
    if (!canEdit && version) {
      // Redirect to remove version param (shows published)
      navigate({
        to: '.',
        search: {},
        replace: true,
      })
    }
  }, [canEdit, version, navigate])

  const { data: loadedDocumentData } = useSuspenseQuery({
    ...documentByIdOptions(idToken!, TENANT_ID!, projectId!, docId!),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'PROCESSING' || status === 'PENDING'
        ? PROCESSING_POLL_INTERVAL
        : false
    },
  })

  // Determine if we're viewing draft or a specific version
  const hasBeenPublished = loadedDocumentData.currentVersion !== undefined
  const isViewingDraft = version === 'draft'
  const versionNumber =
    version && version !== 'draft' ? parseInt(version, 10) : null
  const isViewingOlderVersion = !!versionNumber

  // Fetch specific version if version param is provided (and not draft)
  // Only fetch versions for users with edit permissions
  const { data: versionData } = useQuery({
    ...documentVersionHistoryByVersionOptions(
      idToken!,
      TENANT_ID!,
      projectId!,
      docId!,
      versionNumber!,
    ),
    enabled: canEdit && !!versionNumber && !isNaN(versionNumber),
  })

  // Determine which document data to display based on version priority
  const displayDocumentData = useMemo(() => {
    // For users without edit permissions, only show published version
    if (!canEdit) {
      if (loadedDocumentData.published) {
        return {
          ...loadedDocumentData,
          content: loadedDocumentData.published.content,
        }
      }
      // Fallback to content if no published version
      return loadedDocumentData
    }

    // If viewing draft explicitly, show draft (only for users with edit permissions)
    if (
      isViewingDraft &&
      loadedDocumentData.hasDraft &&
      loadedDocumentData.draft
    ) {
      return {
        ...loadedDocumentData,
        content: loadedDocumentData.draft,
      }
    }

    // If version param exists and version data is loaded, use that
    if (versionNumber && versionData) {
      return versionData
    }

    // Default: show published. When working content differs from the published
    // snapshot (e.g. after in-place refine), show the unpublished working copy.
    if (hasUnpublishedContentChanges(loadedDocumentData)) {
      return loadedDocumentData
    }

    if (loadedDocumentData.published) {
      return {
        ...loadedDocumentData,
        content: loadedDocumentData.published.content,
      }
    }

    return loadedDocumentData
  }, [canEdit, isViewingDraft, versionNumber, versionData, loadedDocumentData])
  const videoRefIds = useMemo(
    () =>
      loadedDocumentData.documentRefs
        ?.filter((ref) => ref.mimeType.includes('video'))
        ?.map((ref) => ref.id) ?? [],
    [loadedDocumentData.documentRefs],
  )

  const hasVideoRefs = videoRefIds.length > 0

  // Single query for videoRefs with conditional polling while transcoding.
  // Using staleTime: 0 ensures fresh data on every render — avoids a flash
  // where stale data briefly shows transcode as RUNNING then immediately updates.
  const { data: videoRefs } = useSuspenseQuery({
    ...referenceDocumentsByIDsOptions(
      idToken!,
      TENANT_ID!,
      projectId!,
      videoRefIds,
      0,
    ),
    refetchInterval: (query) => {
      const refs = query.state.data
      // Keep polling until ALL video refs have transcode status SUCCEEDED.
      // The status may be undefined, PENDING, PROCESSING, RUNNING, etc.
      const allReady = refs?.every(
        (ref) => ref.metadata?.transcode?.status === 'SUCCEEDED',
      )
      return allReady ? false : PROCESSING_POLL_INTERVAL
    },
  })

  // Video is only ready when ALL refs have transcode SUCCEEDED.
  // Any other status (undefined, PENDING, PROCESSING, RUNNING) = not ready.
  const allTranscodeReady =
    !hasVideoRefs ||
    videoRefs.every((ref) => ref.metadata?.transcode?.status === 'SUCCEEDED')
  const generateRunning =
    loadedDocumentData.status === 'PROCESSING' ||
    loadedDocumentData.status === 'PENDING'

  const computeState = useCallback((): DocumentState => {
    // Only SOP generation blocks the document view.
    // Video transcode runs in parallel and can take 10-20 min — it's shown
    // as a non-blocking banner via PostProcessBanner once the SOP is ready.
    if (generateRunning) {
      const phases: ProcessingPhase[] = [
        {
          id: 'generate',
          label: 'Generating SOP',
          description: 'AI is analyzing the video and creating steps',
          status: 'active',
        },
      ]

      return { type: 'processing', phases }
    } else if (loadedDocumentData.status === 'ERROR') {
      return {
        type: 'error',
        message: loadedDocumentData.errorMessage ?? 'Unknown error',
      }
    } else {
      return { type: 'ready' }
    }
  }, [
    generateRunning,
    loadedDocumentData.status,
    loadedDocumentData.errorMessage,
  ])

  const state = useMemo((): DocumentState => computeState(), [computeState])
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)

  const initialRender = useRef(true) // Used for checking if the document is being rerendered or it is a new navigation

  const videoSourceIds = useMemo(
    () =>
      Array.from(
        new Set(
          displayDocumentData.content?.steps
            ?.filter((step) => step.type === 'video')
            ?.map((step) => step.source!!) ?? [],
        ),
      ),
    [displayDocumentData.content?.steps],
  )

  // Skip while transcode is still running — the playlist endpoint would return 400 "Video has
  // not been transcoded yet". The player tries the CDN endpoint first; on 404 (CDN not
  // configured in this env) it falls back to the legacy GCS-signed-URL endpoint.
  const videoSrcMap = useMemo<Record<string, VideoSrcUrls> | undefined>(() => {
    if (!allTranscodeReady || videoSourceIds.length === 0) return undefined
    const docUrl = (source: string) =>
      `${BASE_URL}/api/org/${TENANT_ID}/projects/${projectId}/documents/${source}`
    return Object.fromEntries(
      videoSourceIds.map((source) => [
        source,
        {
          primary: `${docUrl(source)}/cdn-transcoded-video.m3u8`,
          secondary: `${docUrl(source)}/transcoded-video.m3u8`,
        },
      ]),
    )
  }, [allTranscodeReady, videoSourceIds, TENANT_ID, projectId])

  const {
    setDocumentData,
    documentData,
    isEditing,
    setEditing,
    setTranscodeRunning,
  } = useDocumentStore()

  const updateViewCountMutation = useMutation({
    mutationFn: (currentCount: number | undefined) => {
      const viewCount = currentCount ?? 0 + 1
      return updateSOP({
        authState: authState!,
        tenantId: TENANT_ID!,
        projectId: loadedDocumentData.projectId,
        sopId: loadedDocumentData.id,
        data: {
          metadata: {
            ...loadedDocumentData.metadata,
            views: viewCount,
          },
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byId(loadedDocumentData.id),
      })
    },
  })

  const checkLastViewed = useLastViewedFromLocal({
    docId: loadedDocumentData.id,
    userId: authState?.uid ?? '',
    incrementViewCountCallback: () => {
      updateViewCountMutation.mutate(
        documentData?.metadata?.views as number | undefined,
      )
    },
  })

  // Checking if the document is being rerendered or it is a new navigation
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false
      setEditing(false)
      checkLastViewed()
    }
  }, [checkLastViewed, setEditing])

  // Poll embedding status after document is ready (non-blocking)
  const firstVideoRefId = videoRefIds[0]
  const { data: embeddingStatus } = useQuery({
    ...documentEmbeddingStatusOptions(
      idToken!,
      TENANT_ID!,
      projectId!,
      firstVideoRefId ?? '',
      state.type === 'ready',
    ),
    enabled: state.type === 'ready' && hasVideoRefs,
  })

  useEffect(() => {
    setTranscodeRunning(!allTranscodeReady)
  }, [allTranscodeReady, setTranscodeRunning])

  // Sync store when document is ready (polling uses refetchInterval on useSuspenseQuery)
  useEffect(() => {
    if (state.type === 'ready' && !isEditing) {
      setDocumentData(displayDocumentData)
    }
  }, [state.type, displayDocumentData, isEditing, setDocumentData])

  return (
    <div className="flex min-h-screen w-full flex-col">
      <VideoPlayerScope videoSrcMap={videoSrcMap ?? {}}>
        <TopNavDocumentEdit
          tenantId={TENANT_ID!}
          isVersionHistoryOpen={isVersionHistoryOpen}
          setIsVersionHistoryOpen={setIsVersionHistoryOpen}
          canPublish={canPublish}
        />
        <main
          className={cn('main-container overflow-y-auto', isMobile && 'mt-15')}
        >
          <SidebarProvider
            name="version-history"
            sidebarWidth={'300px'}
            open={isVersionHistoryOpen}
            onOpenChange={(open) => setIsVersionHistoryOpen(open)}
            ref={documentContainerRef}
          >
            <div className="flex w-full flex-col gap-4">
              <DraftModeBanner
                isViewingDraft={isViewingDraft}
                showPublishedVersionLink={hasBeenPublished}
                showNoPublishedVersionMessage={!hasBeenPublished}
              />
              <OlderVersionBanner
                isViewingOlderVersion={isViewingOlderVersion}
              />
              <div className="document-container">
                <DocumentEditHeader
                  documentData={displayDocumentData}
                  baseDocumentData={loadedDocumentData}
                />
                {state.type === 'ready' ? (
                  <>
                    <PostProcessBanner
                      transcodeRunning={!allTranscodeReady}
                      embeddingStatus={embeddingStatus?.embeddingStatus}
                      ragStatus={loadedDocumentData.rag?.status}
                    />
                    <Outlet />
                  </>
                ) : null}
                {state.type === 'processing' ? (
                  <ProcessingStepper phases={state.phases} />
                ) : null}
                {state.type === 'error' ? (
                  <>
                    <div className="flex h-1/3 w-full flex-col items-center gap-4">
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>
                          {state.message}
                          <span className="text-copy-secondary text-sm">
                            Please try again or contact support.
                          </span>
                        </AlertDescription>
                      </Alert>
                    </div>
                    {/* Diagnostics is the only child route allowed to render
                       under the ERROR gate so failed-generation cases can
                       still be inspected. The error banner above stays
                       visible so the user keeps seeing what's wrong. */}
                    {onDiagnosticsTab && <Outlet />}
                  </>
                ) : null}
              </div>
            </div>
            {canEdit && (
              <VersionHistorySidebar
                open={isVersionHistoryOpen}
                onOpenChange={setIsVersionHistoryOpen}
              />
            )}
          </SidebarProvider>
        </main>
      </VideoPlayerScope>
    </div>
  )
}
